import { useTranslation } from 'react-i18next';

export default function OverseasPricingSection() {
  const { t } = useTranslation();

  const pricingPlans = [
    {
      category: 'Initial Setup',
      price: '¥350,000',
      suffix: '~',
      description: 'One-time comprehensive setup fee',
      features: [
        'License application support',
        'Property inspection coordination',
        'OTA platform registration',
        'Initial listing optimization',
        'Professional photography',
        'Welcome guide creation'
      ],
      highlight: false
    },
    {
      category: 'Operations Management',
      price: '15%',
      suffix: 'of revenue',
      description: 'Monthly management fee',
      features: [
        '24/7 guest communication',
        'Dynamic pricing optimization',
        'Booking management',
        'Review management',
        'Monthly English reports',
        'Performance analytics'
      ],
      highlight: true
    },
    {
      category: 'Tax Filing Support',
      price: '¥200,000',
      suffix: '~ / year',
      description: 'Annual tax filing service',
      features: [
        'Non-resident tax filing',
        'Income tax calculation',
        'Deduction optimization',
        'Tax authority communication',
        'Annual tax report',
        'Compliance assurance'
      ],
      highlight: false
    },
    {
      category: 'Monthly Tax Advisory',
      price: '¥50,000',
      suffix: '~ / month',
      description: 'Ongoing tax consultation',
      features: [
        'Monthly tax consultation',
        'Quarterly tax planning',
        'Expense tracking support',
        'Tax optimization advice',
        'Regulation updates',
        'Priority support'
      ],
      highlight: false
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-white/10 text-white text-sm font-semibold rounded-full mb-4">
            TRANSPARENT PRICING
          </div>
          <h2 className="text-5xl font-bold text-white mb-6">
            Clear, Straightforward Pricing
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            No hidden fees. No surprises. Just honest pricing for professional service.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {pricingPlans.map((plan, index) => (
            <div
              key={index}
              className={`rounded-2xl p-8 ${
                plan.highlight
                  ? 'bg-white shadow-2xl border-4 border-amber-400'
                  : 'bg-white/5 backdrop-blur-sm border border-white/10'
              }`}
            >
              {plan.highlight && (
                <div className="inline-block px-4 py-1 bg-amber-400 text-gray-900 text-xs font-bold rounded-full mb-4">
                  MOST POPULAR
                </div>
              )}

              <h3 className={`text-2xl font-bold mb-2 ${plan.highlight ? 'text-gray-900' : 'text-white'}`}>
                {plan.category}
              </h3>

              <div className="mb-4">
                <span className={`text-5xl font-bold ${plan.highlight ? 'text-gray-900' : 'text-white'}`}>
                  {plan.price}
                </span>
                <span className={`text-xl ml-2 ${plan.highlight ? 'text-gray-600' : 'text-gray-400'}`}>
                  {plan.suffix}
                </span>
              </div>

              <p className={`mb-8 ${plan.highlight ? 'text-gray-600' : 'text-gray-400'}`}>
                {plan.description}
              </p>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className={`ri-check-line text-lg mt-0.5 ${plan.highlight ? 'text-green-600' : 'text-green-400'}`}></i>
                    <span className={plan.highlight ? 'text-gray-700' : 'text-gray-300'}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                className={`block w-full py-4 rounded-lg font-semibold text-center transition-all whitespace-nowrap cursor-pointer ${
                  plan.highlight
                    ? 'bg-gray-900 text-white hover:bg-gray-800'
                    : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                Get Started
              </a>
            </div>
          ))}
        </div>

        {/* Additional Notes */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <i className="ri-information-line"></i>
            Important Notes
          </h4>
          <ul className="space-y-3 text-gray-300">
            <li className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
              <span>All prices are subject to property size, location, and specific requirements</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
              <span>Cleaning fees are charged separately based on property size (typically ¥8,000-15,000 per cleaning)</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
              <span>Custom packages available for multiple properties or unique requirements</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-arrow-right-s-line text-amber-400 mt-1"></i>
              <span>Free initial consultation to assess your specific needs and provide accurate quote</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}